import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Wind, Droplets, Shield, Sparkles, Check } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

export const HaloterapiaPage = () => {
  const [content, setContent] = useState({});
  const [contentLoaded, setContentLoaded] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content`);
        const pageContent = {};
        response.data.forEach(item => {
          if (item.page === 'haloterapia') {
            pageContent[item.section] = item;
          }
        });
        setContent(pageContent);
      } catch (error) {
      } finally {
        setContentLoaded(true);
      }
    };
    fetchContent();
  }, []);

  const heroContent = content.hero || {};
  const whatContent = content.what || {};

  return (
    <div className="bg-halo-cream" data-testid="haloterapia-page">
      {/* Hero */}
      <section className="relative py-24 bg-halo-charcoal overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          {contentLoaded && heroContent.image_url && (
            <img
              src={heroContent.image_url}
              alt=""
              className="w-full h-full object-cover"
            />
          )}
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div {...fadeInUp} className="max-w-3xl">
            <span className="text-halo-brown font-medium tracking-widest text-sm">
              {heroContent.subtitle || '¿QUÉ ES LA HALOTERAPIA?'}
            </span>
            <h1 className="font-heading text-5xl md:text-6xl text-halo-cream mt-4 mb-6">
              {heroContent.title || 'Haloterapia en HALO'}
            </h1>
            <p className="text-halo-cream/80 text-lg leading-relaxed">
              {heroContent.text || 'Respiración, piel y sistema nervioso: un enfoque integral del bienestar'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="font-heading text-4xl text-halo-charcoal">
                Una terapia natural con <span className="text-halo-brown italic">historia</span>
              </h2>
              <p className="text-halo-text leading-relaxed text-lg">
                La haloterapia es una terapia natural basada en la inhalación de 
                micropartículas de sal mineral en un ambiente controlado. Aunque hoy 
                se integra en centros de bienestar contemporáneos, su origen se remonta 
                a los estudios realizados en cuevas de sal en Europa del Este durante 
                el siglo XIX.
              </p>
              <p className="text-halo-text leading-relaxed">
                Los trabajadores de minas de sal presentaban una menor incidencia de 
                enfermedades respiratorias, lo que llevó a investigar los beneficios 
                terapéuticos de la sal micronizada.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {contentLoaded && whatContent.image_url && (
                <img
                  src={whatContent.image_url}
                  alt="Cueva de sal"
                  className="rounded-2xl w-full h-[400px] object-cover"
                />
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Properties */}
      <section className="py-24 bg-halo-surface">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl text-halo-charcoal mb-4">
              Propiedades de la sal micronizada
            </h2>
            <p className="text-halo-text max-w-2xl mx-auto">
              La sal posee propiedades únicas que benefician al sistema respiratorio y la piel
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Antiinflamatoria',
                description: 'Reduce la inflamación de las vías respiratorias y la piel'
              },
              {
                icon: Sparkles,
                title: 'Antibacteriana',
                description: 'Propiedades naturales que ayudan a limpiar las vías aéreas'
              },
              {
                icon: Droplets,
                title: 'Mucolítica',
                description: 'Ayuda a fluidificar la mucosidad, facilitando su expulsión'
              },
              {
                icon: Wind,
                title: 'Higroscópica',
                description: 'Atrae humedad, favoreciendo la limpieza respiratoria'
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-halo-cream p-8 rounded-2xl text-center border border-halo-taupe/10"
              >
                <div className="w-16 h-16 bg-halo-brown/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <item.icon size={28} className="text-halo-brown" />
                </div>
                <h3 className="font-heading text-xl text-halo-charcoal mb-3">
                  {item.title}
                </h3>
                <p className="text-halo-text text-sm">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who benefits */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-halo-brown font-medium tracking-widest text-sm">
              BENEFICIOS
            </span>
            <h2 className="font-heading text-4xl text-halo-charcoal mt-4 mb-4">
              ¿A quién puede beneficiar la haloterapia?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Alergia estacional y rinitis',
                description: 'La acción mucolítica de la sal puede ayudar a descongestionar las vías respiratorias superiores y favorecer una respiración más limpia en épocas de polen.'
              },
              {
                title: 'Problemas respiratorios leves',
                description: 'Bronquitis infantil y adulta, congestión frecuente, procesos respiratorios repetitivos o sensación de "respiración corta" pueden beneficiarse del ambiente salino.'
              },
              {
                title: 'Bronquitis infantil',
                description: 'En niños con procesos respiratorios recurrentes, la haloterapia puede actuar como complemento no invasivo que favorece la limpieza bronquial.'
              },
              {
                title: 'Ansiedad y estrés',
                description: 'La mejora de la respiración está directamente vinculada con la regulación del sistema nervioso, facilitando estados de calma.'
              },
              {
                title: 'Rosácea, psoriasis y pieles atópicas',
                description: 'La exposición a ambientes salinos secos puede favorecer la mejora de ciertas afecciones cutáneas leves.'
              },
              {
                title: 'Fortalecimiento respiratorio',
                description: 'La práctica regular puede contribuir a mantener las vías respiratorias más limpias, especialmente en personas expuestas a contaminación.'
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-halo-surface p-8 rounded-2xl border border-halo-taupe/10"
              >
                <h3 className="font-heading text-xl text-halo-charcoal mb-4">
                  {item.title}
                </h3>
                <p className="text-halo-text text-sm leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-16 bg-halo-brown/10">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center space-y-6"
          >
            <h3 className="font-heading text-2xl text-halo-charcoal">
              Enfoque responsable
            </h3>
            <p className="text-halo-text leading-relaxed">
              La haloterapia no sustituye tratamientos médicos, pero puede actuar como 
              complemento natural dentro de un estilo de vida saludable. En HALO trabajamos 
              desde una perspectiva responsable, priorizando la información clara y el 
              bienestar consciente.
            </p>
            <p className="text-halo-text leading-relaxed">
              En HALO, la haloterapia no se presenta como un tratamiento médico, sino como 
              un complemento natural de apoyo al bienestar respiratorio, cutáneo y nervioso, 
              integrado dentro de un espacio diseñado para potenciar sus efectos.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-halo-charcoal">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h2 className="font-heading text-4xl text-halo-cream">
              Experimenta los beneficios de la haloterapia
            </h2>
            <p className="text-halo-cream/70 max-w-xl mx-auto">
              Reserva tu primera sesión y descubre cómo la sal puede transformar 
              tu bienestar respiratorio.
            </p>
            <Link
              to="/bonos"
              className="inline-flex items-center gap-2 bg-halo-brown text-halo-cream px-8 py-4 rounded-full font-medium hover:bg-halo-charcoal transition-colors"
              data-testid="haloterapia-reservar-btn"
            >
              Comprar bono
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HaloterapiaPage;
